import { dataSourceService } from '../../../../services/dataSourceService';
import { widgetRegistry } from '../../registry';
import type { WidgetType } from '../../types';
import { useWidgetData } from './useWidgetData';
import type { UseWidgetDataOptions, UseWidgetDataResult } from './useWidgetData';

/**
 * Shared hook for widgets that consume data from the data-provider pipeline.
 *
 * Reads the `dataProvider` binding from the widget's registry definition,
 * so each widget doesn't need its own service file. The connection between
 * a frontend widget type and the data-provider widget name lives in one place:
 * the widget's `definition.dataProvider.widget` field.
 *
 * @param widgetType  - The frontend widget type (e.g. 'price')
 * @param ticker      - The asset identifier to pass as shared param
 * @param config      - The widget's config object; keys from dataProvider.params are forwarded
 * @param options     - Standard useWidgetData options (enabled, widgetInstanceId, etc.)
 */
export function useDataProviderWidget<T>(
  widgetType: WidgetType,
  ticker: string | undefined,
  config: Record<string, unknown>,
  options?: UseWidgetDataOptions,
): UseWidgetDataResult<T> {
  const definition = widgetRegistry.get(widgetType);

  if (!definition?.dataProvider) {
    throw new Error(
      `Widget type "${widgetType}" has no dataProvider binding in its definition. ` +
      `Add a dataProvider field to the widget definition in registry.ts.`
    );
  }

  const { widget: dpWidget, params: paramDefs } = definition.dataProvider;

  // Build per-widget params from the param definitions declared in the definition.
  // Uses the config value if provided, otherwise falls back to the declared default.
  const widgetParams: Record<string, unknown> = {};
  if (paramDefs) {
    for (const param of paramDefs) {
      const val = config[param.key];
      if (val !== undefined && val !== null && val !== '') {
        widgetParams[param.key] = val;
      } else if (param.defaultValue !== undefined && param.defaultValue !== '') {
        widgetParams[param.key] = param.defaultValue;
      }
    }
  }

  const hasWidgetParams = Object.keys(widgetParams).length > 0;

  // Build stable dependency array from the params that actually matter
  const paramDeps = paramDefs
    ? paramDefs.map((p) => config[p.key] ?? p.defaultValue ?? null)
    : [];

  // Live-refresh by default: charts/data re-pull every 5 min without a manual
  // reload (react-query only polls while the tab is focused, and the provider
  // caches keep most polls cheap). Callers can override via options.
  const refreshOptions: UseWidgetDataOptions = {
    autoRefresh: true,
    refreshInterval: 300,
    ...options,
  };

  return useWidgetData<T>(
    async (signal) => {
      const response = await dataSourceService.fetchWidgets(
        hasWidgetParams ? { [dpWidget]: widgetParams } : [dpWidget],
        { ticker: ticker! },
        signal,
      );

      // Surface auth / provider errors clearly
      if (response.errors) {
        const widgetError = response.errors[dpWidget];
        const authError = response.errors.auth;
        if (authError) {
          throw new Error('No data provider credentials configured. Add provider credentials in Settings to fetch live data.');
        }
        if (widgetError) {
          throw new Error(widgetError);
        }
      }

      const widgetData = response.widgets[dpWidget];
      if (widgetData === undefined) {
        throw new Error(`No data returned for ${dpWidget}. Check that your data provider credentials are configured.`);
      }

      return widgetData as T;
    },
    [ticker, ...paramDeps],
    refreshOptions,
  );
}
